import React, { useEffect, useState, useCallback } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { retrieveStudyDropdownsSpecific, retrieveStudySchedulesDropdowns } from '../../../../services/StudyService';
import { linkClientToStudy } from '../../../../services/ClientService';

import { formatDateAsString } from '../../../../util/StringUtil';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './LinkClientToStudy.css';

export const LinkClientToStudy = ({
  client,
  context,
  study,
  navigateTo,
  navigateToWith,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  closeModal,
}) => {
  const [clientName, setClientName] = useState('');
  const [clientIdOrPassportNumber, setClientIdOrPassportNumber] = useState('');
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState('');
  const [id, setId] = useState('');
  const [idType, setIdType] = useState('Participant');
  const [status, setStatus] = useState({ label: 'In-Screening', value: 'In-Screening' });
  const [statusOptions, setStatusOptions] = useState(['In-Screening', 'Enrolled']);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [studyRegistrationDate, setStudyRegistrationDate] = useState(new Date());
  const [studyError, setStudyError] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  const [participantError, setParticipantError] = useState('');
  const [dateError, setDateError] = useState('');
  const [studyName, setStudyName] = useState('');

  const retrieveStudies = useCallback(async () => {
    setSpinnerText('Retrieving Studies');
    const [studyData, studyError] = await retrieveStudyDropdownsSpecific(context.username, true);
    if (studyData) {
      if (studyData.responseStatus === 'Success') {
        const formattedStudies = studyData.dropdownStudyVOs.map((study) => ({
          value: study,
          label: `${study.name}`,
        }));
        setStudies(formattedStudies);
      } else {
        showToaster(studyData);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: studyError.message,
      });
    }
    setSpinnerText(null);
  }, [context, setSpinnerText, showToaster]);

  const retrieveStudySchedules = useCallback(
    async (studyExternalReference) => {
      setSpinnerText('Retrieving Study Schedules');

      const [scheduleData, scheduleError] = await retrieveStudySchedulesDropdowns(
        studyExternalReference,
        context.username
      );
      if (scheduleData) {
        if (scheduleData.responseStatus === 'Success') {
          const formattedSchedules = scheduleData.dropdownStudyScheduleVOS.map((schedule) => ({
            value: schedule,
            label: `${schedule.name}`,
          }));
          setSchedules(formattedSchedules);
        } else {
          showToaster(scheduleData);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: scheduleError.message,
        });
      }
      setSpinnerText(null);
    },
    [context, setSpinnerText, showToaster]
  );

  useEffect(() => {
    if (Object.keys(client).length !== 0) {
      setClientName(client.firstName + ' ' + client.lastName || '');
      setClientIdOrPassportNumber(client.idNumber || client.saIdNumber || client.passportNumber || '');
    }
    if (!study) {
      retrieveStudies();
    } else {
      setStudyName(study.protocolID + ' ' + study.studyName + ' ' + study.facility);
      retrieveStudySchedules(study.studyExternalReference);
    }
  }, [client, study, retrieveStudies, retrieveStudySchedules]);

  const handleStudySelection = (selectedStudyOption) => {
    setSelectedStudy(selectedStudyOption);
    retrieveStudySchedules(selectedStudyOption?.value?.studyExternalReference);
  };

  const validateFields = () => {
    let isValid = true;

    if (!study && !selectedStudy) {
      setStudyError('*REQUIRED');
      isValid = false;
    } else {
      setStudyError('');
    }

    if (!selectedSchedule) {
      setScheduleError('*REQUIRED');
      isValid = false;
    } else {
      setScheduleError('');
    }

    if (!id) {
      setParticipantError('*REQUIRED');
      isValid = false;
    } else {
      setParticipantError('');
    }

    if (!studyRegistrationDate) {
      setDateError('*REQUIRED');
      isValid = false;
    } else if (studyRegistrationDate > new Date()) {
      setDateError('*Date cannot be future dated.');
      isValid = false;
    } else {
      setDateError('');
    }

    if (client.consents) {
      let hasResearchStudiesConsent = false;
      let hasClinicalTrialsConsent = false;

      for (const consent of client.consents) {
        if (consent.consentType === 'researchStudiesConsent' && consent.consentValue) {
          hasResearchStudiesConsent = true;
        }
        if (consent.consentType === 'clinicalTrialsConsent' && consent.consentValue) {
          hasClinicalTrialsConsent = true;
        }
      }

      if (!hasResearchStudiesConsent && !hasClinicalTrialsConsent) {
        isValid = false;
        showToaster({
          responseStatus: 'Error',
          responseMessage: 'The client does not have clinical trial and research studies consent.',
        });
      }
    }

    return isValid;
  };

  const handleLinkClientClick = () => {
    const validated = validateFields();
    if (!validated) {
      return;
    }

    const clientToLink = {
      clientExternalReference: client.externalReference,
      locationExternalReference: selectedStudy?.value?.locationExternalReference || study?.facilityExternalReference,
      studyExternalReference: selectedStudy?.value?.studyExternalReference || study?.studyExternalReference,
      studyRegistrationDate: formatDateAsString(studyRegistrationDate),
      status: status.value,
      id,
      idType,
      studyScheduleExternalReference: selectedSchedule?.value?.studyScheduleExternalReference,
    };

    linkClient(clientToLink);
  };

  const handleIdTypeChange = (idType) => {
    setIdType(idType);
    setStatusOptions(idType === 'Participant' ? ['In-Screening', 'Enrolled'] : ['In-Screening']);

    if (idType === 'Screening') {
      setStatus({ label: 'In-Screening', value: 'In-Screening' });
    }
  };

  const handleStatusChange = (status) => {
    setStatus(status);
  };

  const handleScheduleChange = (selectedSchedule) => {
    setSelectedSchedule(selectedSchedule);
  };

  const linkClient = async (clientToLinkVO) => {
    setSpinnerText('Linking Client To Study');
    const [data, error] = await linkClientToStudy(context.username, clientToLinkVO);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (context.navigateContext) {
          navigateToWith('studyTrialEdit', 'studyTrialGeneral', context.navigateContext);
        } else if (context.currentScreen === 'participantsLinkNew') {
          await navigateTo('participants');
        } else {
          await closeModal();
        }
      } else {
        showToaster(data);
        if (data.responseMessage.includes('does not allow for co-enrolment.')) {
          setStudyError('Co-enrolment not allowed for selected study.');
        }
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  return (
    <div className="link-client-to-study">
      <div className="link-client-to-study-content">
        <div className="input-container">
          <label className={'input-label'}>Client Name:</label>
          <input
            className="text-input"
            disabled={true}
            type="text"
            value={clientName}
            onChange={({ target }) => {
              setClientName(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientName123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Client Name:'}
            />
          )}
        </div>

        <div className="input-container">
          <label className={'input-label'}>SA ID / Passport No.:</label>
          <input
            className="text-input"
            disabled={true}
            type="text"
            value={clientIdOrPassportNumber}
            onChange={({ target }) => {
              setClientIdOrPassportNumber(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'clientIdOrPassportNumber123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'SA ID / Passport No.:'}
            />
          )}
        </div>

        {!study && (
          <div>
            <div className="input-container">
              <label className={'input-label'}>Protocol ID:</label>
              <Select
                options={studies}
                isClearable
                closeMenuOnSelect={true}
                onChange={handleStudySelection}
                value={selectedStudy || null}
                classNamePrefix={`input-select${studyError && 'error-border'}`}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'linkClientProtocolId'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Protocol ID:'}
                />
              )}
            </div>
            <div className="error-message">{studyError && <div className="small-red-text">{studyError}</div>}</div>
          </div>
        )}

        {study && (
          <div className="input-container">
            <label className={'input-label'}>Protocol ID:</label>
            <input className="text-input" disabled={true} type="text" value={studyName} />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'linkClientProtocolId'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Protocol ID:'}
              />
            )}
          </div>
        )}

        <div>
          <div className="input-container">
            <label className={'input-label'}>ID:</label>
            <input
              className={`text-input ${participantError && 'error-border'}`}
              type="text"
              value={id}
              onChange={({ target }) => {
                setId(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'participantId123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'ID:'}
              />
            )}
          </div>
          <div className="error-message">
            {participantError && <div className="small-red-text">{participantError}</div>}
          </div>
        </div>

        <div>
          <div className="input-container">
            <label className={'input-label'}>Type of ID:</label>
            <div className="link-client-to-study-id-type-container">
              <Checkbox
                checked={idType === 'Participant'}
                onChange={() => handleIdTypeChange('Participant')}
                color="#6F8695"
                fillStyle
              />
              <label className="input-label">Participant</label>
            </div>
            <div className="link-client-to-study-id-type-container">
              <Checkbox
                checked={idType === 'Screening'}
                onChange={() => handleIdTypeChange('Screening')}
                color="#6F8695"
                fillStyle
              />
              <label className="input-label">Screening</label>
            </div>
          </div>
        </div>

        <div>
          <div className="input-container">
            <label className={'input-label'}>Participant Status:</label>
            <Select
              options={statusOptions.map((option) => ({ label: option, value: option }))}
              closeMenuOnSelect={true}
              onChange={handleStatusChange}
              value={status || null}
              classNamePrefix={`input-select${studyError && 'error-border'}`}
              isDisabled={idType === 'Screening'}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'linkClientParticipantStatus'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Participant Status:'}
              />
            )}
          </div>
          <div className="error-message">{studyError && <div className="small-red-text">{studyError}</div>}</div>
        </div>

        <div>
          <div className="input-container">
            <label className={'input-label'}>Study Schedule:</label>
            <Select
              options={schedules}
              isClearable
              closeMenuOnSelect={true}
              onChange={handleScheduleChange}
              value={selectedSchedule || null}
              classNamePrefix={`input-select${scheduleError && 'error-border'}`}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'linkClientStudySchedule'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Study Schedule:'}
              />
            )}
          </div>
          <div className="error-message">{scheduleError && <div className="small-red-text">{scheduleError}</div>}</div>
        </div>

        <div className="input-container">
          <label className={'input-label'}>Study Registration Date:</label>
          <DatePicker
            selected={studyRegistrationDate}
            className={`text-input ${dateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            onChange={(selectedDate) => setStudyRegistrationDate(selectedDate)}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyRegistrationDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study Registration Date:'}
            />
          )}
        </div>
        <div className="error-message">{dateError && <div className="small-red-text">{dateError}</div>}</div>
      </div>

      <div className="link-client-to-study-button-container">
        <SaveButton
          title="Cancel"
          textColor={'#78a480'}
          color={'#ffffff'}
          border={'1px solid #78a480'}
          onClick={closeModal}
        />
        <SaveButton
          title="Link"
          textColor={'#ffffff'}
          color={'#78a480'}
          border={'1px solid #78a480'}
          onClick={handleLinkClientClick}
        />
      </div>
    </div>
  );
};
